import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Parameter } from '@shared/types';
import { Input } from '@/components/ui/input';
import {
  getParameterIcon,
  calculateParameterRange,
} from '@/utils/parameterUtils';
import { ParameterLabel } from '@/components/parameter/ParameterLabel';

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

const TAU = Math.PI * 2;

/**
 * Convert a parameter angle (degrees, 0 = 12-o'clock, clockwise) to SVG polar
 * coordinates relative to a centre (cx, cy) with radius r.
 */
function polarToXY(
  angleDeg: number,
  cx: number,
  cy: number,
  r: number,
): { x: number; y: number } {
  // SVG: 0° at 3-o'clock, clockwise. Our convention: 0° at 12-o'clock, so shift by -90°.
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/**
 * Build an SVG arc path from startDeg to endDeg (both in our convention).
 * sweepDeg is endDeg - startDeg, already computed by the caller.
 */
function arcPath(
  startDeg: number,
  sweepDeg: number,
  cx: number,
  cy: number,
  r: number,
): string {
  const absSpan = Math.abs(sweepDeg);

  // Full circle: draw two semicircles so the SVG arc command works
  if (absSpan >= 360) {
    const top = polarToXY(startDeg, cx, cy, r);
    const bot = polarToXY(startDeg + 180, cx, cy, r);
    return (
      `M ${top.x} ${top.y} ` +
      `A ${r} ${r} 0 1 1 ${bot.x} ${bot.y} ` +
      `A ${r} ${r} 0 1 1 ${top.x} ${top.y}`
    );
  }

  const start = polarToXY(startDeg, cx, cy, r);
  const end = polarToXY(startDeg + sweepDeg, cx, cy, r);
  const largeArc = absSpan > 180 ? 1 : 0;
  // Clockwise sweep for positive sweepDeg
  const sweep = sweepDeg >= 0 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`;
}

function snapToStep(value: number, step: number | undefined): number {
  if (!step || step <= 0) return value;
  return Math.round(value / step) * step;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SIZE = 48; // SVG canvas size (px)
const CX = SIZE / 2;
const CY = SIZE / 2;
const R_TRACK = SIZE / 2 - 4; // background track radius
const R_FILL = R_TRACK; // filled arc radius (same as track)
const R_HANDLE = SIZE / 2 - 2; // handle dot sits on the outer edge

export function AngleInput({
  param,
  onCommit,
}: {
  param: Parameter;
  onCommit: (value: Parameter['value']) => void;
}) {
  const { min, max } = calculateParameterRange(param);
  const step =
    typeof param.range?.step === 'number' ? param.range.step : undefined;
  const isFullCircle = max - min >= 360;

  const rawValue =
    typeof param.value === 'number' ? param.value : Number(param.value);
  const [localValue, setLocalValue] = useState<number>(rawValue);
  const [inputText, setInputText] = useState<string>(fmt(rawValue));

  // Sync when parent pushes a new value
  const prevRef = useRef(param.value);
  if (prevRef.current !== param.value) {
    prevRef.current = param.value;
    const v =
      typeof param.value === 'number' ? param.value : Number(param.value);
    setLocalValue(v);
    setInputText(fmt(v));
  }

  // Throttled commit (200 ms, matches ParameterSlider pattern)
  const lastCommitTime = useRef(0);
  const commitTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const throttledCommit = useCallback(
    (v: number) => {
      const now = Date.now();
      const doCommit = () => {
        onCommit(v);
        lastCommitTime.current = Date.now();
        commitTimeout.current = null;
      };
      if (now - lastCommitTime.current >= 200) {
        doCommit();
      } else {
        if (commitTimeout.current) clearTimeout(commitTimeout.current);
        commitTimeout.current = setTimeout(
          doCommit,
          200 - (now - lastCommitTime.current),
        );
      }
    },
    [onCommit],
  );

  const immediateCommit = useCallback(
    (v: number) => {
      if (commitTimeout.current) {
        clearTimeout(commitTimeout.current);
        commitTimeout.current = null;
      }
      onCommit(v);
      lastCommitTime.current = Date.now();
    },
    [onCommit],
  );

  useEffect(() => {
    return () => {
      if (commitTimeout.current) clearTimeout(commitTimeout.current);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Pointer interaction
  // -------------------------------------------------------------------------
  const svgRef = useRef<SVGSVGElement>(null);
  const isDragging = useRef(false);

  const angleFromPointer = useCallback(
    (clientX: number, clientY: number): number => {
      const el = svgRef.current;
      if (!el) return localValue;
      const rect = el.getBoundingClientRect();
      const px = clientX - rect.left;
      const py = clientY - rect.top;
      // Scale pointer to SVG coords
      const scaleX = SIZE / rect.width;
      const scaleY = SIZE / rect.height;
      const dx = px * scaleX - CX;
      const dy = py * scaleY - CY;

      // atan2 gives angle from 3-o'clock; shift +90° so 0 is at 12-o'clock
      let deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
      // Normalise to [0, 360)
      deg = ((deg % 360) + 360) % 360;

      if (isFullCircle) {
        // For a full circle, allow free rotation. Map into [min, min+360) so
        // the value stays in the expected domain.
        // Find the closest equivalent angle to current value (avoids jumps)
        const base = min;
        const raw = base + ((deg - (((base % 360) + 360) % 360) + 360) % 360);
        return snapToStep(raw, step);
      }

      // For a partial arc, map the raw [0,360) angle back to [min,max].
      // The arc starts at `min` (relative to 12-o'clock) so we offset deg
      // by min before clamping.
      const span = max - min;
      // Rotate deg so that `min` maps to 0
      const startSvg = ((min % 360) + 360) % 360;
      let relative = (deg - startSvg + 360) % 360;
      // If the span < 360, clamp relative to [0, span]
      relative = clamp(relative, 0, span);
      return snapToStep(min + relative, step);
    },
    [isFullCircle, localValue, min, max, step],
  );

  const applyAngle = useCallback(
    (clientX: number, clientY: number, immediate: boolean) => {
      const v = angleFromPointer(clientX, clientY);
      setLocalValue(v);
      setInputText(fmt(v));
      if (immediate) immediateCommit(v);
      else throttledCommit(v);
    },
    [angleFromPointer, immediateCommit, throttledCommit],
  );

  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    isDragging.current = true;
    applyAngle(e.clientX, e.clientY, false);
  };

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!isDragging.current) return;
    applyAngle(e.clientX, e.clientY, false);
  };

  const handlePointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    applyAngle(e.clientX, e.clientY, true);
  };

  // -------------------------------------------------------------------------
  // Text input
  // -------------------------------------------------------------------------
  const commitText = () => {
    const n = parseFloat(inputText);
    if (isNaN(n)) {
      setInputText(fmt(localValue));
      return;
    }
    const v = isFullCircle ? n : snapToStep(clamp(n, min, max), step);
    setLocalValue(v);
    setInputText(fmt(v));
    immediateCommit(v);
  };

  // -------------------------------------------------------------------------
  // SVG arc geometry
  // -------------------------------------------------------------------------

  // Background track: full span from min to max
  const trackPath = arcPath(
    min,
    isFullCircle ? 360 : max - min,
    CX,
    CY,
    R_TRACK,
  );

  // Filled arc: from min to current value
  const fillSpan = isFullCircle
    ? (localValue - min + 360) % 360 || (isDragging.current ? 360 : 0)
    : clamp(localValue - min, 0, max - min);
  const fillPath = fillSpan > 0 ? arcPath(min, fillSpan, CX, CY, R_FILL) : null;

  // Handle dot position
  const handleAngle = isFullCircle ? localValue : clamp(localValue, min, max);
  const handle = polarToXY(handleAngle, CX, CY, R_HANDLE);

  const IconComponent = getParameterIcon(param.name);

  return (
    <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
      <ParameterLabel param={param} IconComponent={IconComponent} />
      <div className="flex w-full items-center gap-3">
        {/* Arc dial */}
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width={SIZE}
          height={SIZE}
          className="shrink-0 cursor-crosshair touch-none select-none overflow-visible"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {/* Background track */}
          <path
            d={trackPath}
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.15"
            strokeWidth="3"
            strokeLinecap="round"
          />
          {/* Filled arc (accent colour) */}
          {fillPath && (
            <path
              d={fillPath}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="3"
              strokeLinecap="round"
            />
          )}
          {/* Handle dot */}
          <circle
            cx={handle.x}
            cy={handle.y}
            r="3.5"
            fill="hsl(var(--primary))"
            stroke="hsl(var(--background))"
            strokeWidth="1.5"
          />
          {/* Centre dot */}
          <circle
            cx={CX}
            cy={CY}
            r="1.5"
            fill="currentColor"
            fillOpacity="0.3"
          />
        </svg>

        {/* Numeric input */}
        <div className="flex flex-shrink-0 items-center gap-2">
          <Input
            className="h-7 w-14 rounded-md px-2 text-center text-xs"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onFocus={(e) => e.target.select()}
            onBlur={commitText}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
          />
          <span className="w-4 text-left text-xs text-muted-foreground">°</span>
        </div>
      </div>
    </div>
  );
}

// Format a number trimming unnecessary trailing decimals
function fmt(n: number): string {
  return String(parseFloat(n.toPrecision(7)));
}
